import * as React from "react";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { formatDate } from "@/lib/datetime";
import { cn } from "@/lib/utils";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0")
);

// Same value shape every call site already used for a plain
// <input type="datetime-local">: "YYYY-MM-DDTHH:mm" in local time. Callers
// (NewRequest.tsx, the advance form) parse/format this string exactly as
// before via toIso()/new Date(...) - only the picker UI changes here, not
// the data flow, so this drops in without touching submit logic.
function parseLocal(datetimeLocal: string): Date | undefined {
  if (!datetimeLocal) return undefined;
  const d = new Date(datetimeLocal);
  return Number.isNaN(d.getTime()) ? undefined : d;
}

function toLocalString(date: Date, timeHHmm: string): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const [h, m] = timeHHmm.split(":").map(Number);
  const withTime = new Date(date);
  withTime.setHours(h || 0, m || 0, 0, 0);
  return `${withTime.getFullYear()}-${pad(withTime.getMonth() + 1)}-${pad(withTime.getDate())}T${pad(withTime.getHours())}:${pad(withTime.getMinutes())}`;
}

export function DateTimePicker({
  value,
  onChange,
  placeholder = "Pick a date & time",
  disabled,
  maxDate,
  minDate
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** When set, days after this date are unselectable in the calendar (e.g.
   *  Reimbursement's Departure/Arrival - you can only claim a trip that has
   *  already happened, so neither date can be in the future). Omit for a
   *  picker that should allow any date (e.g. Fixed Allowance, advances). */
  maxDate?: Date;
  /** When set, days before this date are unselectable (e.g. an advance's
   *  Departure/Arrival - raised for an upcoming trip, so neither date
   *  should be in the past). Omit for a picker where a past date is
   *  expected (e.g. Reimbursement, which claims a trip that already
   *  happened). */
  minDate?: Date;
}) {
  const [open, setOpen] = React.useState(false);
  const selected = parseLocal(value);
  const time = selected
    ? `${String(selected.getHours()).padStart(2, "0")}:${String(selected.getMinutes()).padStart(2, "0")}`
    : "09:00";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full min-w-0 justify-start font-normal",
            !selected && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          <span className="truncate">
            {selected ? `${formatDate(selected)} ${time}` : placeholder}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            if (date) onChange(toLocalString(date, time));
          }}
          disabled={[
            ...(maxDate ? [{ after: maxDate }] : []),
            ...(minDate ? [{ before: minDate }] : [])
          ]}
          autoFocus
        />
        <div className="flex items-center gap-2 border-t p-3">
          {/* Two own Selects instead of a native <input type="time"> - the
              browser's own time picker can't be styled or z-index-layered
              reliably against this Popover (its native dropdown rendered
              detached from the field and covered its own clock icon), so
              this keeps the whole control inside our own component tree. */}
          <Select
            value={time.split(":")[0]}
            onValueChange={(h) => {
              const base = selected ?? new Date();
              onChange(toLocalString(base, `${h}:${time.split(":")[1]}`));
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {HOURS.map((h) => (
                <SelectItem key={h} value={h}>
                  {h}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-muted-foreground">:</span>
          <Select
            value={time.split(":")[1]}
            onValueChange={(m) => {
              const base = selected ?? new Date();
              onChange(toLocalString(base, `${time.split(":")[0]}:${m}`));
            }}
          >
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {MINUTES.map((m) => (
                <SelectItem key={m} value={m}>
                  {m}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
}
